import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface PostcodeLookupResult {
  street: string;
  city: string;
  lat: number;
  lng: number;
}

export function usePostcodeLookup() {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<PostcodeLookupResult | null>(null);

  const lookup = useCallback(async (postalCode: string, houseNumber: string): Promise<PostcodeLookupResult | null> => {
    const normalizedPostalCode = postalCode.replace(/\s+/g, "").toUpperCase();
    const normalizedHouseNumber = houseNumber.trim();

    if (normalizedPostalCode.length < 6 || !normalizedHouseNumber) {
      setResult(null);
      return null;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("postcode-lookup", {
        body: {
          postalCode: normalizedPostalCode,
          houseNumber: normalizedHouseNumber,
        },
      });

      if (error) {
        throw error;
      }

      const lookupResult = ((data as { result?: PostcodeLookupResult | null } | null)?.result ?? null) as PostcodeLookupResult | null;
      setResult(lookupResult);
      return lookupResult;
    } catch (error) {
      setResult(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clear = useCallback(() => setResult(null), []);

  return { lookup, result, isLoading, clear };
}
